import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TaskListItem } from './TaskListItem';
import { TaskBulkActionBar } from './TaskBulkActionBar';
import { BulkFailureDialog } from './BulkFailureDialog';
import type { TaskBulkActionsApi } from './use-task-bulk-actions';
import type { TaskWithRelations } from '@vagusx/clients';
import type { ClassificationLabelMap } from '@/hooks/use-classification-label';

/**
 * Task list + pagination only. The top toolbar (scope tabs, search,
 * filter, refresh) is rendered by `TaskListHeader`, which the parent
 * mounts as a sibling above this panel.
 *
 * Bulk-action machinery (selection state, mutation, toasts, escape
 * handler, stale-selection guard) is owned by `useTaskBulkActions`,
 * called once in `TasksInboxPage` and passed down via the `bulk`
 * prop. Lifting it lets the toolbar's "Select" toggle drive the same
 * state the rows + action bar render against.
 */
interface TaskListPanelProps {
  tasks: TaskWithRelations[];
  total: number;
  isLoading: boolean;
  selectedTaskId: string | undefined;
  onSelectTask: (id: string) => void;
  onToggleStar?: (id: string, isStarred: boolean) => void;
  labelMap: ClassificationLabelMap;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  bulk: TaskBulkActionsApi;
}

export function TaskListPanel({
  tasks,
  total,
  isLoading,
  selectedTaskId,
  onSelectTask,
  onToggleStar,
  labelMap,
  page,
  pageSize,
  onPageChange,
  bulk,
}: TaskListPanelProps): React.ReactElement {
  const totalPages = Math.ceil(total / pageSize);

  // Title lookup for the failure dialog. Keyed on id so the dialog
  // can show "Refill request – Jane Doe" instead of a raw UUID.
  const titleById = useMemo(() => new Map(tasks.map((t) => [t.id, t.title])), [tasks]);

  return (
    <div className="flex h-full flex-col">
      {/* Task list. `min-h-0` is load-bearing: without it the flex
          item's `min-height: auto` lets the list grow to fit content
          (152 items on a long page = tall), the parent expands past
          its bounded height, and the pagination footer below gets
          pushed off-screen. Same gotcha as InteractionDetailSheet's
          transcript section. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <TaskBulkActionBar
          count={bulk.selection.count}
          onMarkDone={bulk.onMarkDone}
          onClear={bulk.selection.clear}
          isPending={bulk.isPending}
        />
        {isLoading ? (
          <div className="space-y-1 p-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-2 py-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            ))}
          </div>
        ) : tasks.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
            No tasks found
          </div>
        ) : (
          <div className="divide-y">
            {tasks.map((task, index) => (
              <TaskListItem
                key={task.id}
                task={task}
                isSelected={task.id === selectedTaskId}
                onClick={() => onSelectTask(task.id)}
                onToggleStar={onToggleStar}
                labelMap={labelMap}
                bulkChecked={bulk.selection.isSelected(task.id)}
                selectionMode={bulk.selectionMode}
                onBulkCheckboxClick={(e) => bulk.onCheckboxClick(task.id, index, e)}
              />
            ))}
          </div>
        )}
      </div>

      <BulkFailureDialog
        open={bulk.failureRows.length > 0}
        onOpenChange={(open) => !open && bulk.closeFailureDialog()}
        failed={bulk.failureRows}
        titleById={titleById}
      />

      {/* Pagination — `flex-shrink-0` so the footer keeps its natural
          height even when the scroll container above is under pressure. */}
      {totalPages > 1 && (
        <div className="flex flex-shrink-0 items-center justify-between border-t px-3 py-2">
          <span className="text-xs text-muted-foreground">
            {total} task{total !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              Prev
            </Button>
            <span className="text-xs text-muted-foreground">
              {page}/{totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
